import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewAccessorQuestionGuidance from '@app/src/components/Drawer/Views/DrawerViewAccessorQuestionGuidance'
import { QuestionItemAnswer } from '@app/src/components/Table/Cells/AnswerWithFlagCell'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import type { Provider } from '@app/src/types/organizations'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import { InfoOutlined } from '@mui/icons-material'
import { IconButton, Paper, Stack, Typography } from '@mui/material'
import React from 'react'
import AssessmentAnswerValue from './AssessmentAnswerValue'
import BaselineAnswer from './BaselineAnswer'
import ClassificationScores from './ClassificationScores'
import type { AnswerRow } from './assessmentAnswersLogic'

interface AssessmentAnswerRowProps {
  row: AnswerRow
  provider: Provider | undefined
  isBaseline: boolean
  showScoring: boolean
  maxRowsForLongAnswers: number
}

const AssessmentAnswerRow: React.FC<AssessmentAnswerRowProps> = ({
  row,
  provider,
  isBaseline,
  showScoring,
  maxRowsForLongAnswers,
}) => {
  const { openDrawer } = useDrawer()
  const { trackEvent } = useAmplitude()
  const { item, rules, displayNumber } = row
  const question = item.requestItem.template
  const title = question?.kpiName || question?.questionText || item.requestItem.description

  const openQuestionGuidance = () => {
    if (!question) return
    trackEvent({
      name: AmplitudeTrackingEvents.Analyze.Assessment.ViewedPage,
      eventProps: { page: 'questionGuidance', questionId: question.id, questionText: title },
    })
    openDrawer(<DrawerViewAccessorQuestionGuidance question={question} />, { anchor: 'left' })
  }

  return (
    <Paper elevation={0} sx={{ border: 1, borderColor: 'grey.300' }}>
      <Stack p={2} spacing={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={openQuestionGuidance}
          sx={{ cursor: question ? 'pointer' : 'default', '&:hover': { color: 'primary.main' } }}
        >
          <IconButton size="small" aria-label={title}>
            <InfoOutlined fontSize="small" />
          </IconButton>
          {displayNumber && (
            <Typography variant="subtitle1" whiteSpace="nowrap">
              {displayNumber}
            </Typography>
          )}
          <Typography variant="subtitle1">{title}</Typography>
        </Stack>
        <AssessmentAnswerValue item={item} provider={provider} showSelectedCount={!isBaseline}>
          {isBaseline ? (
            <BaselineAnswer item={item} rules={rules} showPoints={showScoring} />
          ) : (
            <QuestionItemAnswer
              item={item}
              maxRows={maxRowsForLongAnswers}
              responderName={provider?.name}
              skippedAnswerAsCard
            />
          )}
        </AssessmentAnswerValue>
        {showScoring && !isBaseline && <ClassificationScores item={item} rules={rules} />}
      </Stack>
    </Paper>
  )
}

export default AssessmentAnswerRow
